import { AfterViewInit, Component, Inject, ViewChild, ViewContainerRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { IDataService } from '../../services/data/data-service.interface';
import { EventsServiceInterface } from '../../services/events/events.interface';
import { IModalsService, IMODALS_SERVICE } from '@platform500services/p500-ui-kit';
import { IConfigurationData, IConfigurationService } from '../../services/configuration/configuration-service.interface';
import { DOCUMENT } from '@angular/common';
import { UserResponse } from '../../services/auth/responses/user.response';
import { DestroySubscribers } from '../../decorators/destroy-subscribers.decorator';
import { AuthServiceInterface } from '../../services/auth/auth-service.interface';
import { WelcomeModal } from '../../modals/welcome/welcome.modal';
import { NgModuleRef } from '@angular/core';
import { IOnboardingTipsService } from '../../services/onboarding-tips/onboarding-tips.interface';
import { TermsComponent } from '../../../modules/account-protection/account-protection/widgets/terms/terms.component';
import { CrmQuestionsComponent } from '../../../modules/account-protection/account-protection/widgets/crm-questions/crm-questions.component';
import { QuestionsModal } from '../../modals/questions/question.modal';
import { LastStepComponent } from '../../../modules/account-protection/account-protection/widgets/kyc/last-step/last-step.component';
import { RbacServiceInterface } from '../../services/rbac/rbac-service.interface';
import { fromEvent, interval, timer } from 'rxjs';
import { BrowserWindowRef } from '../../services/window/window.service';
import { HttpClient } from '@angular/common/http';
import { NewVersionModal } from '../../modals/new-version/new-version.modal';
import { RbacPermissionsConstant } from '../../services/rbac/rbac-permissions.constant';
import { InfoModal } from '../../modals/info/info.modal';
import { IEnvironment } from '../../services/environment/environment.interface';
import { UserActivityService } from '../../services/user-activity/user-activity.service';

@Component({
  templateUrl: './base.layout.html',
})
@DestroySubscribers()
export class BaseLayoutComponent implements AfterViewInit {
  @ViewChild('app', { read: ViewContainerRef }) app: ViewContainerRef;
  @ViewChild('appContainer', { read: ViewContainerRef }) appContainer: ViewContainerRef;
  public isShowSidebar = false;
  public isOverlay = false;
  public isLoaded = false;
  public isTourOpened = false;
  public isCollapsedSidebar: boolean;
  public offline = false;
  public RbacPermissionsConstant = RbacPermissionsConstant;

  private subscribers: any = {};
  private currentMainJsHash = '';

  constructor(
    @Inject(IMODALS_SERVICE) private modalsService: IModalsService,
    @Inject('IDataService') private dataService: IDataService,
    @Inject('AuthServiceInterface') private authService: AuthServiceInterface,
    @Inject('IConfigurationService') private configurationService: IConfigurationService,
    @Inject('EventsServiceInterface') private eventsService: EventsServiceInterface,
    @Inject(DOCUMENT) private document: HTMLDocument,
    private activatedRoute: ActivatedRoute,
    private moduleRef: NgModuleRef<any>,
    @Inject('IOnboardingTipsService') private onboardingTipsService: IOnboardingTipsService,
    @Inject('RbacServiceInterface') protected rbacService: RbacServiceInterface,
    private window: BrowserWindowRef,
    private http: HttpClient,
    @Inject('IEnvironmentService') private envService: IEnvironment,
    private userActivityService: UserActivityService
  ) {
    this.subscribers.isShowSidebar = this.eventsService.on('sidebarComponent:toggle').subscribe(() => {
      this.isShowSidebar = !this.isShowSidebar;
      this.isOverlay = !this.isOverlay;
    });
    this.activatedRoute.data.subscribe((data: { userData: UserResponse; dataData: any; configurationData: IConfigurationData }) => {
      this.authService.setUser(data.userData);
      this.dataService.setData(data.dataData);
      this.configurationService.setData(data.configurationData);
      this.document.getElementById('app-favicon').setAttribute('href', data.userData.logo || '');
      this.isLoaded = true;

      if (
        this.getWindowWidth() > 1000 &&
        this.onboardingTipsService.getWorkingStatus() &&
        this.onboardingTipsService.getWelcomeModalStatus()
      ) {
        setTimeout(() => {
          this.modalsService.open(
            WelcomeModal,
            {
              data: {
                name: data.userData.first_name,
              },
            },
            this.moduleRef
          );
        }, 1000);
      }
    });
    this.subscribers.isCollapsedNavigation = this.eventsService.on('sidebarComponent:collapsed-toggle').subscribe((value: boolean) => {
      this.isCollapsedSidebar = value;
    });
    this.setOfflineModeHandlers();
    this.initCookiehub();
    this.subscribers.userActivity = this.userActivityService.activityTracker$().subscribe();
  }

  getWindowWidth(): number {
    return window.innerWidth || this.document.documentElement.clientWidth || this.document.getElementsByTagName('body')[0].clientWidth;
  }

  ngAfterViewInit() {
    this.modalsService.target = this.appContainer;
    this.showUnpaidClientModal(5000);
    const termsWidget = new TermsComponent(this.modalsService);
    termsWidget.afterData = () => {
      if (termsWidget.data?.crm_terms.length) {
        const docs = termsWidget.data.crm_terms.filter((item) => !item.agreed);
        if (docs.length) {
          termsWidget.openModal(docs);
        }
      }
    };
    termsWidget.getData();
    const questionWidget = new CrmQuestionsComponent();
    const afterData = questionWidget.afterData.bind(questionWidget);
    questionWidget.afterData = () => {
      afterData();
      if (questionWidget?.answers?.length) {
        const items = questionWidget.answers.filter((item) => !item.answer_created_at);
        if (items.length) {
          this.modalsService.open(
            QuestionsModal,
            {
              items,
            },
            this.moduleRef
          );
        }
      }
    };
    questionWidget.getData();
    const lastStep = new LastStepComponent(this.modalsService, this.dataService, this.rbacService);
    lastStep.getData();
    this.checkAppVersion();
  }

  private setOfflineModeHandlers(): void {
    this.subscribers.offline = fromEvent(this.window.nativeWindow, 'offline').subscribe(() => (this.offline = true));
    this.subscribers.online = fromEvent(this.window.nativeWindow, 'online').subscribe(() => (this.offline = false));

    if (!this.window.nativeWindow.navigator.onLine) {
      this.offline = true;
    }
  }

  private checkAppVersion() {
    const scripts = document.querySelectorAll('script');
    scripts.forEach((script) => {
      if (script.src.includes('main')) {
        const splittedSrc = script.src.split('.');
        this.currentMainJsHash = splittedSrc[splittedSrc.length - 2].split('-')[1];
      }
    });
    this.startFetchVersion();
  }

  private startFetchVersion() {
    const fetchInterval = interval(300000).subscribe(() => {
      this.http.get('', { responseType: 'text' }).subscribe((response) => {
        const reg = /(script src="main-).+(\.js)/g;
        let [newHash] = response.match(reg) || [''];
        newHash = newHash.replace('script src="main-', '').replace('.js', '');
        if (this.currentMainJsHash !== newHash) {
          this.modalsService.open(NewVersionModal, {}, this.moduleRef, false);
          fetchInterval.unsubscribe();
        }
      });
    });
  }

  showUnpaidClientModal(delay: number): void {
    timer(delay).subscribe(() => {
      if (this.dataService.getData().isUnpaid) {
        this.modalsService.open(
          InfoModal,
          {
            data: {
              title: $localize`:@@common.access_temporarily_unavailable:Access Temporarily Unavailable`,
              body: $localize`:@@common.unpaid_affiliate_area:Access to the Affiliate Area is currently restricted. Please reach out to your manager for more details.`,
              subTitle: '',
              footerType: null,
              hasCopy: false,
              modalType: null,
            },
            onClose: () => {
              this.showUnpaidClientModal(2000);
            },
          },
          this.moduleRef
        );
      }
    });
  }

  private initCookiehub(): void {
    const cookieHub = this.envService.getVar('cookieHub');
    if (!cookieHub) {
      return;
    }

    const script = this.document.createElement('script');
    script.src = `https://cdn.cookiehub.eu/c2/${cookieHub}.js`;
    script.type = 'text/javascript';
    script.onload = () => {
      const cpm = {};
      const window = this.window.nativeWindow;
      if (window?.cookiehub && typeof window?.cookiehub?.load === 'function') {
        window['cookiehub'].load(cpm);
      } else {
        console.error('CookieHub is not loaded or initialized.');
      }
    };
    document.body.appendChild(script);
  }
}
